import React, { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Upload, Download, FileSpreadsheet, CheckCircle2, AlertTriangle } from "lucide-react";
import api from "../lib/api";
import { useToast } from "../hooks/use-toast";

const TEMPLATE = "name,nature_of_engagement,period,employee_name,employee_email,client_contact_email\nAcme Pvt Ltd,Statutory Audit,FY 2025-26,Priya Nair,priya@firm.co,cfo@acme.co\nBeta LLP,GST Filing,Q1 FY26,Rahul Menon,rahul@firm.co,accounts@beta.co\n";

const ImportClientsModal = ({ open, onClose, onImported }) => {
  const { toast } = useToast();
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const reset = () => { setFile(null); setResult(null); };
  const close = () => { reset(); onClose(); };

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "clients_template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const submit = async () => {
    if (!file) { toast({ title: "Choose a CSV file first", variant: "destructive" }); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/clients/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      onImported && onImported();
      toast({ title: `Imported ${data.created} client(s)`, description: data.errors?.length ? `${data.errors.length} row(s) skipped` : "All rows processed" });
    } catch (err) {
      toast({ title: "Import failed", description: err?.response?.data?.detail || "Check your CSV and try again.", variant: "destructive" });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="sm:max-w-lg rounded-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-50"><FileSpreadsheet className="h-6 w-6 text-indigo-700" /></div>
          <DialogTitle className="text-2xl font-bold text-[#1a1a2e]">Bulk import clients</DialogTitle>
          <DialogDescription className="text-slate-500">Upload a CSV to create many clients at once. Each row invites the handling employee and client contact automatically.</DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="mt-2 space-y-4">
            <button onClick={downloadTemplate} className="inline-flex items-center gap-2 text-sm font-medium text-[#2f2b8f] hover:underline"><Download className="h-4 w-4" /> Download CSV template</button>
            <div className="rounded-xl bg-slate-50 px-4 py-3 text-xs text-slate-500">
              Required columns: <span className="font-mono">name, nature_of_engagement, period, employee_name, employee_email, client_contact_email</span>
            </div>
            <input ref={inputRef} type="file" accept=".csv" className="hidden" onChange={(e) => setFile(e.target.files[0])} />
            <button onClick={() => inputRef.current?.click()} className="flex w-full flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-slate-200 py-10 text-center transition-colors hover:border-[#2f2b8f]">
              <Upload className="h-7 w-7 text-slate-400" />
              <span className="text-sm font-medium text-[#1a1a2e]">{file ? file.name : "Click to choose a CSV file"}</span>
              <span className="text-xs text-slate-400">.csv up to a few hundred rows</span>
            </button>
            <Button onClick={submit} disabled={busy || !file} className="w-full rounded-xl bg-[#2f2b8f] py-6 text-sm font-semibold text-white hover:bg-[#26236f]">
              {busy ? "Importing..." : "Import clients"}
            </Button>
          </div>
        ) : (
          <div className="mt-2 space-y-4">
            <div className="flex items-center gap-3 rounded-xl bg-emerald-50 px-4 py-3">
              <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              <span className="text-sm font-medium text-emerald-800">{result.created} client(s) created and invited.</span>
            </div>
            {result.errors?.length > 0 && (
              <div className="rounded-xl bg-amber-50 px-4 py-3">
                <div className="mb-2 inline-flex items-center gap-2 text-sm font-semibold text-amber-800"><AlertTriangle className="h-4 w-4" /> {result.errors.length} row(s) skipped</div>
                <ul className="space-y-1 text-xs text-amber-700">
                  {result.errors.slice(0, 8).map((e, i) => <li key={i}>Row {e.row}: {e.error}</li>)}
                </ul>
              </div>
            )}
            <Button onClick={close} className="w-full rounded-xl bg-[#2f2b8f] py-6 text-sm font-semibold text-white hover:bg-[#26236f]">Done</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ImportClientsModal;
